import {FC, useMemo, useEffect} from 'react';
import {map, union, isEmpty, filter, values, uniq, flatten, find, includes} from 'lodash';
import {observer} from 'mobx-react';
import {ValueInput, ValueInputRendererProps} from 'apstra-ui-common';

const telemetryData = 'aos.sdk.telemetry.schemas.iba_data';
const telemetryIntegerData = 'aos.sdk.telemetry.schemas.iba_integer_data';
const telemetryStringData = 'aos.sdk.telemetry.schemas.iba_string_data';
const telemetryGenericData = 'aos.sdk.telemetry.schemas.generic';

const processorDataTypeSchemas = {
  extensible_data_collector: {
    dynamic: [telemetryData, telemetryIntegerData, telemetryStringData],
    static: [telemetryData, telemetryIntegerData, telemetryStringData],
  },
  generic_data_collector: {
    ts: [telemetryGenericData],
    ns: [telemetryGenericData],
    dss: [telemetryGenericData],
  },
};

const ServiceNameInput: FC<ValueInputRendererProps> = observer(({
  name, value, values: {data_type: dataType}, schema, required, disabled, errors, onChange, loaderVisible, fieldProps,
  processor, telemetryServiceRegistryItems
}) => {
  const processorDataTypes = processorDataTypeSchemas[processor.type];

  const options = useMemo(() => {
    let result;
    if (schema?.enum) {
      result = schema.enum;
    } else if (schema?.const) {
      result = [schema.const];
    } else if (processorDataTypes) {
      let matchingServices = telemetryServiceRegistryItems;
      const schemas = processorDataTypes[dataType] ?? uniq(flatten(values(processorDataTypes)));
      matchingServices = filter(matchingServices, (item) => schemas.includes(item.storage_schema_path));
      result = map(matchingServices, 'service_name');
    }
    return !isEmpty(result) ? union(result, value ? [value] : []).sort() : [];
  }, [value, schema, dataType, telemetryServiceRegistryItems, processorDataTypes]);

  useEffect(() => {
    if (dataType && processorDataTypes) {
      const service = find(telemetryServiceRegistryItems, {service_name: value});
      if (processorDataTypes[dataType] && !includes(processorDataTypes[dataType], service?.storage_schema_path)) {
        onChange('');
      }
    }
  }, [dataType, value, onChange, processorDataTypes, telemetryServiceRegistryItems]);

  return (
    <ValueInput
      name={name}
      value={value}
      schema={{...schema, enum: options}}
      required={required}
      disabled={disabled}
      loading={loaderVisible}
      errors={errors}
      onChange={onChange}
      fieldProps={fieldProps}
    />
  );
});

export default ServiceNameInput;
